
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Calendar, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CalendarCredentials } from "@/utils/a2a/types/calendarCredentials";

export const CalendarSettings = () => {
  const [googleCalendarSync, setGoogleCalendarSync] = useState(false);
  const [microsoftCalendarSync, setMicrosoftCalendarSync] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calendarCredentials, setCalendarCredentials] = useState<CalendarCredentials | null>(null);
  const [defaultStartTime, setDefaultStartTime] = useState("09:00");
  const [defaultEndTime, setDefaultEndTime] = useState("17:00");
  const [activeTab, setActiveTab] = useState("google");

  useEffect(() => {
    const fetchCalendarCredentials = async () => {
      try {
        setLoading(true);
        
        // Using raw SQL query to avoid type issues before the types are regenerated
        const { data, error } = await supabase
          .from('calendar_credentials')
          .select('*')
          .maybeSingle();
          
        if (error) {
          console.log('No calendar credentials found', error);
        } else if (data) {
          setCalendarCredentials(data as CalendarCredentials);
          if (data.provider === 'google') {
            setGoogleCalendarSync(true);
          } else if (data.provider === 'microsoft365') {
            setMicrosoftCalendarSync(true);
          }
        }
      } catch (error) {
        console.error('Error fetching calendar credentials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarCredentials();
  }, []);

  const handleConnectGoogleCalendar = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('calendar-auth', {
        body: { provider: 'google' }
      });
      
      if (error) throw error;
      
      // Redirect to Google OAuth consent screen
      window.location.href = data.authUrl;
    } catch (error) {
      console.error('Failed to start Google Calendar auth:', error);
      toast.error('Failed to connect to Google Calendar');
    }
  };

  const handleConnectMicrosoftCalendar = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('calendar-auth', {
        body: { provider: 'microsoft365' }
      });
      
      if (error) throw error;
      
      // Redirect to Microsoft OAuth consent screen
      window.location.href = data.authUrl;
    } catch (error) {
      console.error('Failed to start Microsoft Calendar auth:', error);
      toast.error('Failed to connect to Microsoft Calendar');
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      const { error } = await supabase.functions.invoke('calendar-disconnect', {});
      
      if (error) throw error;
      
      setGoogleCalendarSync(false);
      setMicrosoftCalendarSync(false);
      setCalendarCredentials(null);
      toast.success('Calendar disconnected successfully');
    } catch (error) {
      console.error('Failed to disconnect calendar:', error);
      toast.error('Failed to disconnect calendar');
    }
  };

  const saveDefaultTimes = async () => {
    // This would be saved to user preferences
    toast.success('Default availability times saved');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="space-y-6 p-6 flex items-center justify-center min-h-[300px]">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading calendar settings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <Calendar className="h-5 w-5" /> 
          Calendar Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 p-6">
        {calendarCredentials ? (
          <Alert className="bg-green-100 border-green-200">
            <Calendar className="h-4 w-4 text-green-600" />
            <AlertTitle>Connected to {calendarCredentials.provider === 'google' ? 'Google Calendar' : 'Microsoft Calendar'}</AlertTitle>
            <AlertDescription>
              Your calendar is connected and synchronized. Last updated: {new Date(calendarCredentials.updated_at).toLocaleString()}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="default" className="bg-muted/40">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            <AlertTitle>Calendar not connected</AlertTitle>
            <AlertDescription>
              Connect your calendar to enable scheduling features in the application.
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="google">Google Calendar</TabsTrigger>
            <TabsTrigger value="microsoft">Microsoft Calendar</TabsTrigger>
          </TabsList>
          
          <TabsContent value="google" className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Google Calendar</Label>
                <p className="text-sm text-muted-foreground">
                  Sync your events with Google Calendar
                </p>
              </div>
              <Switch
                checked={googleCalendarSync}
                disabled={microsoftCalendarSync || loading}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleConnectGoogleCalendar();
                  } else {
                    handleDisconnectCalendar();
                  }
                }}
              />
            </div>
            
            {googleCalendarSync && (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Calendar Default Availability</Label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="g-start-time">Start Time</Label>
                      <Input 
                        type="time" 
                        id="g-start-time" 
                        value={defaultStartTime} 
                        onChange={(e) => setDefaultStartTime(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="g-end-time">End Time</Label>
                      <Input 
                        type="time" 
                        id="g-end-time" 
                        value={defaultEndTime}
                        onChange={(e) => setDefaultEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <Button onClick={saveDefaultTimes}>
                  Save Availability Settings
                </Button>
              </div>
            )}

            {!googleCalendarSync && (
              <Button 
                className="mt-2" 
                onClick={handleConnectGoogleCalendar}
                disabled={microsoftCalendarSync}
              >
                Connect Google Calendar
              </Button>
            )}
          </TabsContent>
          
          <TabsContent value="microsoft" className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Microsoft Calendar</Label>
                <p className="text-sm text-muted-foreground">
                  Sync your events with Microsoft 365 Calendar
                </p>
              </div>
              <Switch
                checked={microsoftCalendarSync}
                disabled={googleCalendarSync || loading}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleConnectMicrosoftCalendar();
                  } else {
                    handleDisconnectCalendar();
                  }
                }}
              />
            </div>
            
            {microsoftCalendarSync && (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Calendar Default Availability</Label>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="m-start-time">Start Time</Label>
                      <Input 
                        type="time" 
                        id="m-start-time" 
                        value={defaultStartTime} 
                        onChange={(e) => setDefaultStartTime(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="m-end-time">End Time</Label>
                      <Input 
                        type="time" 
                        id="m-end-time" 
                        value={defaultEndTime}
                        onChange={(e) => setDefaultEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <Button onClick={saveDefaultTimes}>
                  Save Availability Settings
                </Button>
              </div>
            )}

            {!microsoftCalendarSync && (
              <Button 
                className="mt-2" 
                onClick={handleConnectMicrosoftCalendar}
                disabled={googleCalendarSync}
              >
                Connect Microsoft Calendar
              </Button>
            )}
          </TabsContent>
        </Tabs>
        
        {calendarCredentials && (
          <div className="pt-4 border-t border-border/50">
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleDisconnectCalendar}
            >
              Disconnect Calendar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
